import React, { useEffect, useState } from "react";
import axios from "axios";

const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/online-yoga-ttc`;

// Default empty objects for array fields
const defaultCourse = () => ({
  id: Date.now(),
  title: "",
  duration: "",
  certification: "",
  style: "",
  date: "",
  image: null,
  imagePreview: "",
  oldPrice: "",
  newPrice: "",
  isNew: true
});

const defaultTestimonial = () => ({
  name: "",
  time: "",
  text: "",
  rating: 5
});

const defaultBenefit = () => ({
  title: "",
  description: ""
});

const defaultStep = () => ({
  step: "",
  title: "",
  description: ""
});

const createEmptyForm = () => ({
  // Hero Section
  heroWelcomeText: "Welcome to Hatha Yogashram",
  heroTitleLine1: "Your Gateway to",
  heroTitleLine2: "Ancient Wisdom",
  heroSubtitle: "Our Online Yoga Teacher Training Course (TTC) brings the transformative essence of traditional Hatha Yog directly to you, no matter where you are in the world.",
  heroImage: null,
  heroImagePreview: "",
  heroButtonText: "Begin Your Journey",
  heroButtonLink: "/BookingForm",

  // About Courses Section
  aboutSectionLabel: "Our Offerings",
  aboutSectionTitle: "About Our Courses",
  courses: [],

  // Booking Section
  bookingSectionLabel: "Reserve Your Spot",
  bookingSectionTitle: "Booking Courses Now",
  bookingSectionDescription: "At Hatha Yogashram, we believe yoga is more than just a practice—it's a way of life. Nestled in the serene foothills of the Himalayas, our ashram has been a sanctuary for seekers for years.",
  bookingCourses: [],

  // Testimonials Section
  testimonialsSectionLabel: "Student Experiences",
  testimonialsSectionTitle: "What Students are Saying",
  testimonials: [],

  // Why Us Section
  whyUsSectionLabel: "Our Promise",
  whyUsSectionTitle: "Why Choose Us",
  whyUsIntro: "At Hatha Yogashram, we believe yoga is more than just a practice—it's a way of life. Nestled in the serene foothills of the Himalayas, our ashram has been a sanctuary for seekers for years.",
  whyUsImage: null,
  whyUsImagePreview: "",
  whyUsBenefits: [],

  // Certification Section
  certificationLogos: [
    { id: 1, image: null, imagePreview: "", alt: "Yoga Alliance" },
    { id: 2, image: null, imagePreview: "", alt: "RYS 200" },
    { id: 3, image: null, imagePreview: "", alt: "RYS 300" },
    { id: 4, image: null, imagePreview: "", alt: "RYS 500" }
  ],
  certificationParagraph1: "",
  certificationParagraph2: "",
  certificationHighlight: "best yoga teacher training in India",

  // How It Works Section
  howItWorksLabel: "Simple Process",
  howItWorksTitle: "How It Works",
  steps: [],

  // Final CTA Section
  finalCtaTitle: "Ready to Begin?",
  finalCtaText: "Enroll now and take the first step toward becoming a certified yoga teacher with Hatha Yogashram!",
  finalCtaButtonText: "Contact Us Today",
  finalCtaButtonLink: "/contact-us"
});

const resolveAssetUrl = (value) => {
  if (!value || typeof value !== "string") return "";
  if (/^https?:\/\//.test(value)) return value;
  if (value.startsWith("/uploads/")) return `${ENV_API_BASE_URL}${value}`;
  return `${ENV_API_BASE_URL}/uploads/${value}`;
};

const normalizeCourseForSubmit = (course, fallbackId) => ({
  id: course?.id || fallbackId,
  title: course?.title || "",
  duration: course?.duration || "",
  certification: course?.certification || "",
  style: course?.style || "",
  date: course?.date || "",
  image: typeof course?.image === "string" ? course.image : "",
  oldPrice: course?.oldPrice || "",
  newPrice: course?.newPrice || "",
  isNew: Boolean(course?.isNew)
});

const normalizeLogosForSubmit = (logos) =>
  (logos || []).map((logo, index) => ({
    id: logo?.id || index + 1,
    alt: logo?.alt || "",
    image: typeof logo?.image === "string" ? logo.image : ""
  }));

const Section = ({ title, children }) => {
  return (
    <div className="card mb-4 shadow-sm">
      <div className="card-header bg-light fw-bold border-start border-4 border-primary">
        <h5 className="mb-0">{title}</h5>
      </div>
      <div className="card-body">
        {children}
      </div>
    </div>
  );
};

function OnlineYogaTTCForm() {
  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(createEmptyForm());
  const [activeTab, setActiveTab] = useState("hero");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(API);
      setData(Array.isArray(res.data.data) ? res.data.data : []);
    } catch (err) {
      console.error("Fetch error:", err);
      setData([]);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e, field) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          [field]: file,
          [`${field}Preview`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayFileChange = (e, field, index, imageField = "image") => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => {
          const arr = [...(prev[field] || [])];
          if (!arr[index]) arr[index] = {};
          arr[index] = {
            ...arr[index],
            [imageField]: file,
            [`${imageField}Preview`]: reader.result
          };
          return { ...prev, [field]: arr };
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCertLogoFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => {
          const arr = [...prev.certificationLogos];
          arr[index] = {
            ...arr[index],
            image: file,
            imagePreview: reader.result
          };
          return { ...prev, certificationLogos: arr };
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleArrayChange = (field, index, key, value) => {
    const arr = [...(formData[field] || [])];
    if (!arr[index]) arr[index] = {};
    arr[index] = { ...arr[index], [key]: value };
    setFormData({ ...formData, [field]: arr });
  };

  const addArrayItem = (field, defaultItem) => {
    const arr = Array.isArray(formData[field]) ? [...formData[field]] : [];
    setFormData({ 
      ...formData, 
      [field]: [...arr, typeof defaultItem === "function" ? defaultItem() : defaultItem] 
    });
  };

  const removeArrayItem = (field, index) => {
    const arr = [...(formData[field] || [])];
    arr.splice(index, 1);
    setFormData({ ...formData, [field]: arr });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    
    const formDataToSend = new FormData();

    const textFields = [
      "heroWelcomeText",
      "heroTitleLine1",
      "heroTitleLine2",
      "heroSubtitle",
      "heroButtonText",
      "heroButtonLink",
      "aboutSectionLabel",
      "aboutSectionTitle",
      "bookingSectionLabel",
      "bookingSectionTitle",
      "bookingSectionDescription",
      "testimonialsSectionLabel",
      "testimonialsSectionTitle",
      "whyUsSectionLabel",
      "whyUsSectionTitle",
      "whyUsIntro",
      "certificationParagraph1",
      "certificationParagraph2",
      "certificationHighlight",
      "howItWorksLabel",
      "howItWorksTitle",
      "finalCtaTitle",
      "finalCtaText",
      "finalCtaButtonText",
      "finalCtaButtonLink"
    ];

    textFields.forEach((field) => {
      formDataToSend.append(field, formData[field] || "");
    });

    if (formData.heroImage instanceof File) {
      formDataToSend.append("heroImage", formData.heroImage);
    } else if (typeof formData.heroImage === "string") {
      formDataToSend.append("heroImage", formData.heroImage);
    }

    if (formData.whyUsImage instanceof File) {
      formDataToSend.append("whyUsImage", formData.whyUsImage);
    } else if (typeof formData.whyUsImage === "string") {
      formDataToSend.append("whyUsImage", formData.whyUsImage);
    }

    const coursesPayload = (formData.courses || []).map((course, index) =>
      normalizeCourseForSubmit(course, Date.now() + index)
    );
    const bookingCoursesPayload = (formData.bookingCourses || []).map((course, index) =>
      normalizeCourseForSubmit(course, Date.now() + index + 1000)
    );
    const testimonialsPayload = (formData.testimonials || []).map((testimonial) => ({
      name: testimonial?.name || "",
      time: testimonial?.time || "",
      text: testimonial?.text || "",
      rating: Number(testimonial?.rating || 5)
    }));
    const whyUsBenefitsPayload = (formData.whyUsBenefits || []).map((benefit) => ({
      title: benefit?.title || "",
      description: benefit?.description || ""
    }));
    const certificationLogosPayload = normalizeLogosForSubmit(formData.certificationLogos);
    const stepsPayload = (formData.steps || []).map((step) => ({
      step: step?.step || "",
      title: step?.title || "",
      description: step?.description || ""
    }));

    formDataToSend.append("courses", JSON.stringify(coursesPayload));
    formDataToSend.append("bookingCourses", JSON.stringify(bookingCoursesPayload));
    formDataToSend.append("testimonials", JSON.stringify(testimonialsPayload));
    formDataToSend.append("whyUsBenefits", JSON.stringify(whyUsBenefitsPayload));
    formDataToSend.append("certificationLogos", JSON.stringify(certificationLogosPayload));
    formDataToSend.append("steps", JSON.stringify(stepsPayload));

    (formData.courses || []).forEach((course, index) => {
      if (course.image instanceof File) {
        formDataToSend.append(`courses[${index}][image]`, course.image);
      }
    });

    (formData.bookingCourses || []).forEach((course, index) => {
      if (course.image instanceof File) {
        formDataToSend.append(`bookingCourses[${index}][image]`, course.image);
      }
    });

    (formData.certificationLogos || []).forEach((logo, index) => {
      if (logo.image instanceof File) {
        formDataToSend.append(`certificationLogos[${index}][image]`, logo.image);
      }
    });

    try {
      if (editId) {
        await axios.put(`${API}/${editId}`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(API, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      setFormData(createEmptyForm());
      setEditId(null);
      setShowForm(false);
      setActiveTab("hero");
      fetchData();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error saving");
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (item) => {
    const normalized = { ...createEmptyForm(), ...item };
    
    // Handle image previews for existing items
    if (item.heroImage && typeof item.heroImage === 'string') {
      normalized.heroImagePreview = resolveAssetUrl(item.heroImage);
    }
    if (item.whyUsImage && typeof item.whyUsImage === 'string') {
      normalized.whyUsImagePreview = resolveAssetUrl(item.whyUsImage);
    }
    
    // Handle courses images
    if (Array.isArray(item.courses)) {
      normalized.courses = item.courses.map(course => ({
        ...course,
        imagePreview: resolveAssetUrl(course.image || "")
      }));
    }
    
    // Handle booking courses images
    if (Array.isArray(item.bookingCourses)) {
      normalized.bookingCourses = item.bookingCourses.map(course => ({
        ...course,
        imagePreview: resolveAssetUrl(course.image || "")
      }));
    }
    
    // Handle certification logos
    if (Array.isArray(item.certificationLogos)) {
      normalized.certificationLogos = item.certificationLogos.map(logo => ({
        ...logo,
        imagePreview: resolveAssetUrl(logo.image || "")
      }));
    } else {
      normalized.certificationLogos = createEmptyForm().certificationLogos;
    }
    
    if (!Array.isArray(normalized.testimonials)) normalized.testimonials = [];
    if (!Array.isArray(normalized.whyUsBenefits)) normalized.whyUsBenefits = [];
    if (!Array.isArray(normalized.steps)) normalized.steps = [];
    
    setFormData(normalized);
    setEditId(item._id);
    setShowForm(true);
    setActiveTab("hero");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this content?")) return;
    try {
      await axios.delete(`${API}/${id}`);
      fetchData();
    } catch (err) {
      console.error(err);
      alert("Error deleting");
    }
  };

  const tabs = [
    { id: "hero", label: "Hero Section" },
    { id: "about", label: "About Courses" },
    { id: "booking", label: "Booking Section" },
    { id: "testimonials", label: "Testimonials" },
    { id: "whyus", label: "Why Us" },
    { id: "certification", label: "Certification" },
    { id: "howitworks", label: "How It Works" },
    { id: "finalcta", label: "Final CTA" }
  ];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="text-primary">Online Yoga TTC Page Content</h2>
      </div>

      {!showForm && (
        <>
          <div className="mb-3">
            <button 
              className="btn btn-primary" 
              onClick={() => { setFormData(createEmptyForm()); setEditId(null); setShowForm(true); setActiveTab("hero"); }}
            >
              <i className="bi bi-plus-circle me-2"></i>Create New Page Content
            </button>
          </div>
          
          <div className="table-responsive">
            <table className="table table-hover table-bordered">
              <thead className="table-light">
                <tr>
                  <th>S.No</th>
                  <th>Hero Title</th>
                  <th>About Title</th>
                  <th>Booking Title</th>
                  <th>Testimonials Title</th>
                  <th>Why Us Title</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item, index) => (
                  <tr key={item._id}>
                    <td>{index + 1}</td>
                    <td>{item.heroTitleLine1 || "-"}</td>
                    <td>{item.aboutSectionTitle || "-"}</td>
                    <td>{item.bookingSectionTitle || "-"}</td>
                    <td>{item.testimonialsSectionTitle || "-"}</td>
                    <td>{item.whyUsSectionTitle || "-"}</td>
                    <td>
                      <button 
                        className="btn btn-warning btn-sm me-2" 
                        onClick={() => handleEdit(item)}
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </button>
                      <button 
                        className="btn btn-danger btn-sm" 
                        onClick={() => handleDelete(item._id)}
                      >
                        <i className="bi bi-trash"></i> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {showForm && (
        <form onSubmit={handleSubmit}>
          {/* Tab Navigation */}
          <ul className="nav nav-tabs mb-4 flex-wrap">
            {tabs.map(tab => (
              <li className="nav-item" key={tab.id}>
                <button
                  type="button"
                  className={`nav-link ${activeTab === tab.id ? 'active fw-bold' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Hero Section */}
          {activeTab === "hero" && (
            <Section title="Hero Section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Welcome Text</label>
                  <input 
                    type="text" 
                    name="heroWelcomeText" 
                    className="form-control" 
                    value={formData.heroWelcomeText} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Hero Image</label>
                  <input 
                    type="file" 
                    className="form-control" 
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "heroImage")} 
                  />
                  {formData.heroImagePreview && (
                    <div className="mt-2">
                      <img 
                        src={formData.heroImagePreview} 
                        alt="Hero Preview" 
                        className="rounded border" 
                        style={{maxHeight: '100px', maxWidth: '100%'}} 
                      />
                    </div>
                  )}
                </div>
              </div>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Hero Title Line 1</label>
                  <input 
                    type="text" 
                    name="heroTitleLine1" 
                    className="form-control" 
                    value={formData.heroTitleLine1} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Hero Title Line 2 (Highlight)</label>
                  <input 
                    type="text" 
                    name="heroTitleLine2" 
                    className="form-control" 
                    value={formData.heroTitleLine2} 
                    onChange={handleChange} 
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Hero Subtitle</label>
                <textarea 
                  name="heroSubtitle" 
                  className="form-control" 
                  rows={3} 
                  value={formData.heroSubtitle} 
                  onChange={handleChange}
                />
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Button Text</label>
                  <input 
                    type="text" 
                    name="heroButtonText" 
                    className="form-control" 
                    value={formData.heroButtonText} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Button Link</label>
                  <input 
                    type="text" 
                    name="heroButtonLink" 
                    className="form-control" 
                    value={formData.heroButtonLink} 
                    onChange={handleChange} 
                  />
                </div>
              </div>
            </Section>
          )}

          {/* About Courses Section */}
          {activeTab === "about" && (
            <Section title="About Courses Section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Label</label>
                  <input 
                    type="text" 
                    name="aboutSectionLabel" 
                    className="form-control" 
                    value={formData.aboutSectionLabel} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input 
                    type="text" 
                    name="aboutSectionTitle" 
                    className="form-control" 
                    value={formData.aboutSectionTitle} 
                    onChange={handleChange} 
                  />
                </div>
              </div>

              <label className="form-label fw-semibold">Courses</label>
              {(formData.courses || []).map((course, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Title (e.g., 200 Hour TTC)" 
                        value={course.title || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "title", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Duration" 
                        value={course.duration || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "duration", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Certification" 
                        value={course.certification || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "certification", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Style" 
                        value={course.style || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "style", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Date" 
                        value={course.date || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "date", e.target.value)} 
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-2">
                      <input 
                        type="file" 
                        className="form-control form-control-sm" 
                        accept="image/*"
                        onChange={(e) => handleArrayFileChange(e, "courses", i, "image")} 
                      />
                      {course.imagePreview && (
                        <img 
                          src={course.imagePreview} 
                          alt="Preview" 
                          className="mt-2 rounded" 
                          style={{maxHeight: '80px'}} 
                        />
                      )}
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Old Price" 
                        value={course.oldPrice || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "oldPrice", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="New Price" 
                        value={course.newPrice || ""} 
                        onChange={(e) => handleArrayChange("courses", i, "newPrice", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2 d-flex align-items-center">
                      <input 
                        type="checkbox" 
                        className="form-check-input me-2" 
                        checked={course.isNew || false}
                        onChange={(e) => handleArrayChange("courses", i, "isNew", e.target.checked)} 
                      />
                      <label className="form-check-label">Show NEW Badge</label>
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-outline-danger btn-sm mt-2" 
                    onClick={() => removeArrayItem("courses", i)}
                  >
                    <i className="bi bi-trash"></i> Remove Course
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={() => addArrayItem("courses", defaultCourse)}
              >
                <i className="bi bi-plus-circle"></i> Add Course
              </button>
            </Section>
          )}

          {/* Booking Section */}
          {activeTab === "booking" && (
            <Section title="Booking Section">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Section Label</label>
                  <input 
                    type="text" 
                    name="bookingSectionLabel" 
                    className="form-control" 
                    value={formData.bookingSectionLabel} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input 
                    type="text" 
                    name="bookingSectionTitle" 
                    className="form-control" 
                    value={formData.bookingSectionTitle} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Use same courses?</label>
                  <select 
                    className="form-select"
                    onChange={(e) => {
                      if (e.target.value === "yes") {
                        const copiedCourses = formData.courses.map(course => ({
                          ...course,
                          id: Date.now() + Math.random()
                        }));
                        setFormData({...formData, bookingCourses: copiedCourses});
                      }
                    }}
                  >
                    <option value="no">No (configure separately)</option>
                    <option value="yes">Yes (copy from Courses)</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Section Description</label>
                <textarea 
                  name="bookingSectionDescription" 
                  className="form-control" 
                  rows={3} 
                  value={formData.bookingSectionDescription} 
                  onChange={handleChange}
                />
              </div>

              <label className="form-label fw-semibold">Booking Courses</label>
              {(formData.bookingCourses || []).map((course, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Title" 
                        value={course.title || ""} 
                        onChange={(e) => handleArrayChange("bookingCourses", i, "title", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input 
                        type="file" 
                        className="form-control form-control-sm" 
                        accept="image/*"
                        onChange={(e) => handleArrayFileChange(e, "bookingCourses", i, "image")} 
                      />
                      {course.imagePreview && (
                        <img 
                          src={course.imagePreview} 
                          alt="Preview" 
                          className="mt-2 rounded" 
                          style={{maxHeight: '60px'}} 
                        />
                      )}
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Old Price" 
                        value={course.oldPrice || ""} 
                        onChange={(e) => handleArrayChange("bookingCourses", i, "oldPrice", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="New Price" 
                        value={course.newPrice || ""} 
                        onChange={(e) => handleArrayChange("bookingCourses", i, "newPrice", e.target.value)} 
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-outline-danger btn-sm mt-2" 
                    onClick={() => removeArrayItem("bookingCourses", i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={() => addArrayItem("bookingCourses", defaultCourse)}
              >
                Add Booking Course
              </button>
            </Section>
          )}

          {/* Testimonials Section */}
          {activeTab === "testimonials" && (
            <Section title="Testimonials Section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Label</label>
                  <input 
                    type="text" 
                    name="testimonialsSectionLabel" 
                    className="form-control" 
                    value={formData.testimonialsSectionLabel} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input 
                    type="text" 
                    name="testimonialsSectionTitle" 
                    className="form-control" 
                    value={formData.testimonialsSectionTitle} 
                    onChange={handleChange} 
                  />
                </div>
              </div>

              <label className="form-label fw-semibold">Testimonials</label>
              {(formData.testimonials || []).map((testimonial, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Name" 
                        value={testimonial.name || ""} 
                        onChange={(e) => handleArrayChange("testimonials", i, "name", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Time (e.g., a year ago)" 
                        value={testimonial.time || ""} 
                        onChange={(e) => handleArrayChange("testimonials", i, "time", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input 
                        type="number" 
                        className="form-control form-control-sm" 
                        placeholder="Rating (1-5)" 
                        min="1" 
                        max="5"
                        value={testimonial.rating || 5} 
                        onChange={(e) => handleArrayChange("testimonials", i, "rating", parseInt(e.target.value))} 
                      />
                    </div>
                  </div>
                  <div className="mb-2">
                    <textarea 
                      className="form-control form-control-sm" 
                      rows={2} 
                      placeholder="Testimonial text" 
                      value={testimonial.text || ""} 
                      onChange={(e) => handleArrayChange("testimonials", i, "text", e.target.value)} 
                    />
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-outline-danger btn-sm" 
                    onClick={() => removeArrayItem("testimonials", i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={() => addArrayItem("testimonials", defaultTestimonial)}
              >
                Add Testimonial
              </button>
            </Section>
          )}

          {/* Why Us Section */}
          {activeTab === "whyus" && (
            <Section title="Why Us Section">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Section Label</label>
                  <input 
                    type="text" 
                    name="whyUsSectionLabel" 
                    className="form-control" 
                    value={formData.whyUsSectionLabel} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input 
                    type="text" 
                    name="whyUsSectionTitle" 
                    className="form-control" 
                    value={formData.whyUsSectionTitle} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">Section Image</label>
                  <input 
                    type="file" 
                    className="form-control" 
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, "whyUsImage")} 
                  />
                  {formData.whyUsImagePreview && (
                    <img 
                      src={formData.whyUsImagePreview} 
                      alt="Preview" 
                      className="mt-2 rounded" 
                      style={{maxHeight: '80px'}} 
                    />
                  )}
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Intro Paragraph</label>
                <textarea 
                  name="whyUsIntro" 
                  className="form-control" 
                  rows={3} 
                  value={formData.whyUsIntro} 
                  onChange={handleChange}
                />
              </div>

              <label className="form-label fw-semibold">Benefits</label>
              {(formData.whyUsBenefits || []).map((benefit, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-5 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Benefit Title" 
                        value={benefit.title || ""} 
                        onChange={(e) => handleArrayChange("whyUsBenefits", i, "title", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-7 mb-2">
                      <textarea 
                        className="form-control form-control-sm" 
                        rows={2} 
                        placeholder="Benefit Description" 
                        value={benefit.description || ""} 
                        onChange={(e) => handleArrayChange("whyUsBenefits", i, "description", e.target.value)} 
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-outline-danger btn-sm" 
                    onClick={() => removeArrayItem("whyUsBenefits", i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={() => addArrayItem("whyUsBenefits", defaultBenefit)}
              >
                Add Benefit
              </button>
            </Section>
          )}

          {/* Certification Section */}
          {activeTab === "certification" && (
            <Section title="Certification Section">
              <div className="mb-3">
                <label className="form-label fw-semibold">Highlight Text</label>
                <input 
                  type="text" 
                  name="certificationHighlight" 
                  className="form-control" 
                  value={formData.certificationHighlight} 
                  onChange={handleChange} 
                />
              </div>

              <label className="form-label fw-semibold">Certification Logos</label>
              {formData.certificationLogos.map((logo, i) => (
                <div key={logo.id} className="row mb-3 align-items-center">
                  <div className="col-md-5">
                    <input 
                      type="file" 
                      className="form-control form-control-sm" 
                      accept="image/*"
                      onChange={(e) => handleCertLogoFileChange(e, i)} 
                    />
                  </div>
                  <div className="col-md-5">
                    <input 
                      type="text" 
                      className="form-control form-control-sm" 
                      placeholder="Alt Text" 
                      value={logo.alt || ""} 
                      onChange={(e) => {
                        const arr = [...formData.certificationLogos];
                        arr[i].alt = e.target.value;
                        setFormData({ ...formData, certificationLogos: arr });
                      }} 
                    />
                  </div>
                  <div className="col-md-2">
                    {logo.imagePreview && (
                      <img 
                        src={logo.imagePreview} 
                        alt={logo.alt} 
                        style={{maxHeight: '40px'}} 
                        className="rounded"
                      />
                    )}
                  </div>
                </div>
              ))}

              <div className="mb-3 mt-3">
                <label className="form-label fw-semibold">Certification Paragraph 1</label>
                <textarea 
                  name="certificationParagraph1" 
                  className="form-control" 
                  rows={4} 
                  value={formData.certificationParagraph1} 
                  onChange={handleChange}
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold">Certification Paragraph 2</label>
                <textarea 
                  name="certificationParagraph2" 
                  className="form-control" 
                  rows={4} 
                  value={formData.certificationParagraph2} 
                  onChange={handleChange}
                />
              </div>
            </Section>
          )}

          {/* How It Works Section */}
          {activeTab === "howitworks" && (
            <Section title="How It Works Section">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Label</label>
                  <input 
                    type="text" 
                    name="howItWorksLabel" 
                    className="form-control" 
                    value={formData.howItWorksLabel} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Section Title</label>
                  <input 
                    type="text" 
                    name="howItWorksTitle" 
                    className="form-control" 
                    value={formData.howItWorksTitle} 
                    onChange={handleChange} 
                  />
                </div>
              </div>

              <label className="form-label fw-semibold">Steps</label>
              {(formData.steps || []).map((step, i) => (
                <div key={i} className="border p-3 mb-3 rounded bg-light">
                  <div className="row">
                    <div className="col-md-2 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Step # (01, 02...)" 
                        value={step.step || ""} 
                        onChange={(e) => handleArrayChange("steps", i, "step", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input 
                        type="text" 
                        className="form-control form-control-sm" 
                        placeholder="Step Title" 
                        value={step.title || ""} 
                        onChange={(e) => handleArrayChange("steps", i, "title", e.target.value)} 
                      />
                    </div>
                    <div className="col-md-6 mb-2">
                      <textarea 
                        className="form-control form-control-sm" 
                        rows={2} 
                        placeholder="Step Description" 
                        value={step.description || ""} 
                        onChange={(e) => handleArrayChange("steps", i, "description", e.target.value)} 
                      />
                    </div>
                  </div>
                  <button 
                    type="button" 
                    className="btn btn-outline-danger btn-sm" 
                    onClick={() => removeArrayItem("steps", i)}
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button 
                type="button" 
                className="btn btn-outline-secondary" 
                onClick={() => addArrayItem("steps", defaultStep)}
              >
                Add Step
              </button>
            </Section>
          )}

          {/* Final CTA Section */}
          {activeTab === "finalcta" && (
            <Section title="Final CTA Section">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold">CTA Title</label>
                  <input 
                    type="text" 
                    name="finalCtaTitle" 
                    className="form-control" 
                    value={formData.finalCtaTitle} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-8 mb-3">
                  <label className="form-label fw-semibold">CTA Text</label>
                  <input 
                    type="text" 
                    name="finalCtaText" 
                    className="form-control" 
                    value={formData.finalCtaText} 
                    onChange={handleChange} 
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Button Text</label>
                  <input 
                    type="text" 
                    name="finalCtaButtonText" 
                    className="form-control" 
                    value={formData.finalCtaButtonText} 
                    onChange={handleChange} 
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Button Link</label>
                  <input 
                    type="text" 
                    name="finalCtaButtonLink" 
                    className="form-control" 
                    value={formData.finalCtaButtonLink} 
                    onChange={handleChange} 
                  />
                </div>
              </div>
            </Section>
          )}

          <div className="mb-4">
            <button type="submit" className="btn btn-success btn-lg me-2" disabled={uploading}>
              <i className="bi bi-save me-2"></i>
              {uploading ? "Saving..." : (editId ? "Update Content" : "Save Content")}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary btn-lg" 
              onClick={() => { setShowForm(false); setEditId(null); setFormData(createEmptyForm()); setActiveTab("hero"); }}
            >
              <i className="bi bi-x-circle me-2"></i>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default OnlineYogaTTCForm;
